import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../../../context/AuthContext';
import {
  DfcAuthOverlayContext,
  DfcAuthOverlayStep,
} from '../../../../context/DfcAuthOverlayContext';
import useDfcSurface from '../../../../hooks/useDfcSurface';
import useDfcVillageDesktop from '../../../../hooks/useDfcVillageDesktop';
import Register from '../../../../pages/register';
import useFitnessVillageCopy from '../../../../hooks/useFitnessVillageCopy';
import { isValidUser } from '../../../../Utils/validate';
import {
  CloseButton,
  SheetDrawer,
} from '../../../register/authenticationForm/variants/dfcVillageTheme/style';
import { OverlayBody, OverlayDialog } from './style';

interface DfcAuthOverlayProps {
  step: DfcAuthOverlayStep;
  onClose: () => void;
}

/**
 * The auth panel over the current page: a centred dialog from 960px, a bottom sheet below.
 *
 * It routes through the shared `<Register>` — exactly as `CourtAuthGate` does — so the
 * CompleteProfile step and every validation path are the app's own. `redirectToCalendar`
 * is off, so a successful sign in leaves the visitor where they were. The panel renders
 * `inline`; the overlay supplies the chrome, so CompleteProfile gets the same frame.
 */
export const DfcAuthOverlay: React.FC<DfcAuthOverlayProps> = ({ step, onClose }) => {
  const surface = useDfcSurface();
  const isDesktop = useDfcVillageDesktop();
  const copy = useFitnessVillageCopy().auth;

  const body = (
    <OverlayBody>
      <CloseButton surface={surface} aria-label={copy.close} onClick={onClose}>
        <CloseIcon />
      </CloseButton>
      <Register registrationStep={step} redirectToCalendar={false} layout="inline" />
    </OverlayBody>
  );

  if (isDesktop) {
    return (
      <OverlayDialog
        surface={surface}
        open
        onClose={onClose}
        PaperProps={{ 'aria-label': copy.overlayLabel }}
        data-testid="auth-overlay-dialog"
      >
        {body}
      </OverlayDialog>
    );
  }

  return (
    <SheetDrawer
      surface={surface}
      anchor="bottom"
      open
      onClose={onClose}
      PaperProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-label': copy.overlayLabel,
      }}
      transitionDuration={0}
      data-testid="auth-overlay-sheet"
    >
      {body}
    </SheetDrawer>
  );
};

/**
 * Mounts the overlay and hands out `openAuth` through `DfcAuthOverlayContext`. Inert until
 * something calls `openAuth` — and only the Fitness Village theme's controls do — so it is
 * safe to mount for every company.
 *
 * The overlay closes itself once the visitor holds a valid account, and whenever the route
 * changes: a flow that leaves the page (email verification) takes the overlay with it.
 */
const DfcAuthOverlayProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [step, setStep] = useState<DfcAuthOverlayStep | null>(null);
  const { user } = useAuth();
  const { pathname } = useLocation();

  const close = useCallback(() => setStep(null), []);

  useEffect(() => {
    if (isValidUser(user)) setStep(null);
  }, [user]);

  useEffect(() => {
    setStep(null);
  }, [pathname]);

  const value = useMemo(() => ({ openAuth: setStep }), []);

  return (
    <DfcAuthOverlayContext.Provider value={value}>
      {children}
      {step && <DfcAuthOverlay step={step} onClose={close} />}
    </DfcAuthOverlayContext.Provider>
  );
};

export default DfcAuthOverlayProvider;
